import { useState, useEffect, useRef } from "react";
import { motion, useInView } from "framer-motion";
import { BarChart, AlertTriangle, Clock4, Zap, ArrowRight, ChartBar, Target } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { ConsultationModal } from '@/components/ConsultationModal';

const AnimatedCounter = ({
  end,
  duration = 2000,
  prefix = "",
  suffix = "",
  decimals = 0
}: {
  end: number;
  duration?: number;
  prefix?: string;
  suffix?: string;
  decimals?: number;
}) => {
  const [count, setCount] = useState(0);
  const countRef = useRef(null);
  const inView = useInView(countRef, {
    once: true,
    margin: "-100px"
  });
  useEffect(() => {
    if (!inView) return;
    let startTime: number;
    let animationFrame: number;
    const startAnimation = (timestamp: number) => {
      startTime = timestamp;
      animate(timestamp);
    };
    const animate = (timestamp: number) => {
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const currentCount = progress * end;
      setCount(currentCount);
      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };
    animationFrame = requestAnimationFrame(startAnimation);
    return () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
    };
  }, [end, duration, inView]);
  return <span ref={countRef} className="font-bold tabular-nums">
    {prefix}{count.toFixed(decimals)}{suffix}
  </span>;
};

const WhyDJPartners = () => {
  const isMobile = useIsMobile();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.3,
        duration: 0.8
      }
    }
  };
  const itemVariants = {
    hidden: {
      y: 20,
      opacity: 0
    },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6
      }
    }
  };
  return <section id="why-djpartners" className="relative py-16 md:py-24 bg-white overflow-hidden">
    <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
      <motion.div className="text-center mb-12 md:mb-16" initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-100px"
      }} variants={containerVariants}>
        <motion.h2 variants={itemVariants} className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3">
          ¿Por qué D&J Partners?
        </motion.h2>
        <motion.p variants={itemVariants} className="text-gray-600 text-lg max-w-3xl mx-auto">
          En un entorno donde la tecnología puede generar más confusión que resultados, D&J Partners aporta visión, estructura y ejecución para transformar empresas con inteligencia artificial.
        </motion.p>
      </motion.div>

      <motion.div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16" initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-100px"
      }} variants={containerVariants}>
        <motion.div variants={itemVariants} className="bg-gray-100 p-6 rounded-xl border border-gray-200 text-center hover:bg-gray-200 transition-all">
          <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center mx-auto mb-4">
            <BarChart className="w-8 h-8 text-gray-700" />
          </div>
          <h3 className="text-gray-900 text-2xl lg:text-3xl font-bold mb-3">
            <AnimatedCounter end={50} prefix="+" /> empresas
          </h3>
          <p className="text-gray-700">Experiencia real con PYMEs y corporativos de diferentes sectores transformadas exitosamente</p>
        </motion.div>

        <motion.div variants={itemVariants} className="bg-gray-100 p-6 rounded-xl border border-gray-200 text-center hover:bg-gray-200 transition-all">
          <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-8 h-8 text-gray-700" />
          </div>
          <h3 className="text-gray-900 text-2xl lg:text-3xl font-bold mb-3">
            <AnimatedCounter end={70} suffix="%" />
          </h3>
          <p className="text-gray-700">
            de los proyectos fallan. Nosotros evitamos eso con estrategia, foco y acompañamiento experto
          </p>
        </motion.div>

        <motion.div variants={itemVariants} className="bg-gray-100 p-6 rounded-xl border border-gray-200 text-center hover:bg-gray-200 transition-all">
          <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center mx-auto mb-4">
            <Clock4 className="w-8 h-8 text-gray-700" />
          </div>
          <h3 className="text-gray-900 text-2xl lg:text-3xl font-bold mb-3">
            <AnimatedCounter end={5} suffix="x" />
          </h3>
          <p className="text-gray-700">
            mayor ROI estratégico. Las empresas que adoptan IA con visión multiplican su retorno
          </p>
        </motion.div>
      </motion.div>

      <motion.div className="mb-12" initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-100px"
      }} variants={containerVariants}>
        <motion.div variants={itemVariants} className="text-center mb-8">
          <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
            Lo que D&J Partners hace por ti
          </h3>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Transformamos tus procesos empresariales en ventajas competitivas mediante IA, con beneficios tangibles y resultados medibles
          </p>
        </motion.div>

        <motion.div variants={containerVariants} className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <motion.div variants={itemVariants} className="bg-gradient-to-r from-gray-50 to-gray-100 p-6 rounded-xl border border-gray-200 hover:shadow-lg transition-all">
            <div className="flex items-start">
              <div className="bg-gray-200 rounded-full p-3 mr-4">
                <BarChart className="w-6 h-6 text-gray-700" />
              </div>
              <div>
                <h4 className="text-xl font-bold text-gray-900 mb-2">Reducción de Costes Operativos</h4>
                <p className="text-gray-700">Optimizamos tus procesos clave mediante automatización inteligente para reducir tiempos, errores y costes innecesarios en tu operación diaria.</p>
              </div>
            </div>
          </motion.div>

          <motion.div variants={itemVariants} className="bg-gradient-to-r from-gray-50 to-gray-100 p-6 rounded-xl border border-gray-200 hover:shadow-lg transition-all">
            <div className="flex items-start">
              <div className="bg-gray-200 rounded-full p-3 mr-4">
                <ChartBar className="w-6 h-6 text-gray-700" />
              </div>
              <div>
                <h4 className="text-xl font-bold text-gray-900 mb-2">Control Total del Negocio</h4>
                <p className="text-gray-700">Implementamos sistemas de Business Intelligence que te permiten visualizar, analizar y tomar decisiones basadas en datos en tiempo real.</p>
              </div>
            </div>
          </motion.div>

          <motion.div variants={itemVariants} className="bg-gradient-to-r from-gray-50 to-gray-100 p-6 rounded-xl border border-gray-200 hover:shadow-lg transition-all">
            <div className="flex items-start">
              <div className="bg-gray-200 rounded-full p-3 mr-4">
                <Target className="w-6 h-6 text-gray-700" />
              </div>
              <div>
                <h4 className="text-xl font-bold text-gray-900 mb-2">Procesos que Mejoran Contigo</h4>
                <p className="text-gray-700">Evaluamos y rediseñamos tus procesos actuales con metodologías de benchmarking, asegurando mejoras continuas y comparativas con los mejores del sector.</p>
              </div>
            </div>
          </motion.div>

          <motion.div variants={itemVariants} className="bg-gradient-to-r from-gray-50 to-gray-100 p-6 rounded-xl border border-gray-200 hover:shadow-lg transition-all">
            <div className="flex items-start">
              <div className="bg-gray-200 rounded-full p-3 mr-4">
                <Zap className="w-6 h-6 text-gray-700" />
              </div>
              <div>
                <h4 className="text-xl font-bold text-gray-900 mb-2">Tecnología al Servicio de tu Estrategia</h4>
                <p className="text-gray-700">Desplegamos herramientas inteligentes adaptadas a tu modelo de negocio para escalar lo que ya haces bien y anticiparte a los cambios del mercado.</p>
              </div>
            </div>
          </motion.div>
        </motion.div>

        <motion.div variants={itemVariants} className="text-center mt-10">
          <ConsultationModal>
            <button className="inline-flex items-center px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-all group">
              Conoce más sobre nuestro proceso estructurado de desarrollo
              <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
          </ConsultationModal>
        </motion.div>
      </motion.div>
    </div>
  </section>;
};

export default WhyDJPartners;
